import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError, onSessionExpired } from '../lib/apiClient';
import { AuthContext } from './context';
import type { AuthContextValue, LoginCredentials, RegisterInput, Role, User } from './types';

/**
 * Session provider for cookie-based JWT auth against the engine.
 *
 * The engine keeps the tokens in httpOnly cookies the browser attaches
 * automatically, so this provider never holds a credential — it only holds the
 * *identity* those cookies resolve to. That means the engine stays the single
 * source of truth: on every mount we ask GET /auth/me rather than trusting any
 * cached client state, which is what makes a suspended or revoked account lose
 * access immediately instead of at the next full page load.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Guards against setting state after unmount (StrictMode double-mounts in dev).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async (): Promise<User | null> => {
    try {
      const me = await api.get<User>('/auth/me');
      if (mountedRef.current) {
        setUser(me);
        setError(null);
      }
      return me;
    } catch (err) {
      // A 401 here is the normal signed-out case, not an error worth surfacing.
      if (mountedRef.current) {
        setUser(null);
        if (err instanceof ApiError && !err.isUnauthorized) {
          setError(err.message);
        }
      }
      return null;
    }
  }, []);

  // Resolve the session once on mount.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      await refresh();
      if (!cancelled && mountedRef.current) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  // The API client tells us when a refresh-token rotation failed for good.
  useEffect(
    () =>
      onSessionExpired(() => {
        if (mountedRef.current) {
          setUser(null);
          setLoading(false);
        }
      }),
    [],
  );

  const login = useCallback(
    async (credentials: LoginCredentials): Promise<User> => {
      setError(null);
      try {
        // Login sets the cookies but returns only { id, role }; fetch the full
        // identity so every consumer sees one consistent User shape.
        await api.post('/auth/login', credentials);
        const me = await refresh();
        if (!me) throw new ApiError(401, 'Signed in, but the session could not be loaded.');
        return me;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Sign-in failed.';
        if (mountedRef.current) setError(message);
        throw err;
      }
    },
    [refresh],
  );

  const register = useCallback(
    async (input: RegisterInput): Promise<User> => {
      setError(null);
      try {
        await api.post('/auth/register', input);
        const me = await refresh();
        if (!me) throw new ApiError(401, 'Registered, but the session could not be loaded.');
        return me;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Registration failed.';
        if (mountedRef.current) setError(message);
        throw err;
      }
    },
    [refresh],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Even if the call fails the local session must end; the cookies are
      // httpOnly so the server is the only party that can truly clear them,
      // and it already tried.
    } finally {
      if (mountedRef.current) {
        setUser(null);
        setError(null);
      }
    }
  }, []);

  const hasRole = useCallback(
    (...roles: Role[]): boolean => (user ? roles.includes(user.role) : false),
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      error,
      isAuthenticated: user !== null,
      login,
      register,
      logout,
      refresh,
      hasRole,
    }),
    [user, loading, error, login, register, logout, refresh, hasRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
